import {
  VISAIcon,
  MastercardIcon,
  AmexIcon,
  MaestroIcon,
  DiscoverIcon,
  JCBIcon,
  TroyIcon,
  MIRIcon,
  CarnetIcon,
  IconProps,
} from '@pb/components'
import { invertObj } from 'ramda'

export enum CreditCardType {
  Mastercard = 'MASTER',
  Visa = 'VISA',
  Amex = 'AMEX',
  Maestro = 'MAESTRO',
  Discover = 'DISCOVER',
  Jcb = 'JCB',
  Dankort = 'DANKORT',
  Switch = 'SWITCH',
  Diners = 'DINERS',
  Troy = 'TROY',
  Mir = 'MIR',
  Carnet = 'CARNET',
}

export type AdyenCreditCardType =
  | 'mc'
  | 'visa'
  | 'amex'
  | 'maestro'
  | 'discover'
  | 'jcb'
  | 'dankort'
  | 'switch'
  | 'diners'
  | 'troy'
  | 'mir'
  | 'carnet'

// Mapping between BE constants and Adyen SDK
export const CREDIT_CARD_ADYEN_TYPES: Record<CreditCardType, AdyenCreditCardType> = {
  [CreditCardType.Mastercard]: 'mc',
  [CreditCardType.Visa]: 'visa',
  [CreditCardType.Amex]: 'amex',
  [CreditCardType.Maestro]: 'maestro',
  [CreditCardType.Discover]: 'discover',
  [CreditCardType.Jcb]: 'jcb',
  [CreditCardType.Dankort]: 'dankort',
  [CreditCardType.Switch]: 'switch',
  [CreditCardType.Diners]: 'diners',
  [CreditCardType.Troy]: 'troy',
  [CreditCardType.Mir]: 'mir',
  [CreditCardType.Carnet]: 'carnet',
} as const

export const ADYEN_CARD_TYPE_TO_OUR_CONSTANTS_MAP = invertObj(CREDIT_CARD_ADYEN_TYPES) as Record<
  AdyenCreditCardType,
  CreditCardType
>

export type CybersourceCreditCardType =
  | 'mastercard'
  | 'visa'
  | 'amex'
  | 'maestro'
  | 'discover'
  | 'jcb'
  | 'dankort'
  | 'switch'
  | 'diners-club'
  | 'troy'
  | 'mir'
  | 'carnet'

// Mapping between BE constants and Cybersource SDK
export const CREDIT_CARD_CYBERSOURCE_TYPES: Record<CreditCardType, CybersourceCreditCardType> = {
  [CreditCardType.Mastercard]: 'mastercard',
  [CreditCardType.Visa]: 'visa',
  [CreditCardType.Amex]: 'amex',
  [CreditCardType.Maestro]: 'maestro',
  [CreditCardType.Discover]: 'discover',
  [CreditCardType.Jcb]: 'jcb',
  [CreditCardType.Dankort]: 'dankort',
  [CreditCardType.Switch]: 'switch',
  [CreditCardType.Diners]: 'diners-club',
  [CreditCardType.Troy]: 'troy',
  [CreditCardType.Mir]: 'mir',
  [CreditCardType.Carnet]: 'carnet',
} as const

export type PrepareCreditCardType =
  | 'master-card'
  | 'amex'
  | 'visa'
  | 'maestro'
  | 'discover'
  | 'jcb'
  | 'dankort'
  | 'switch'
  | 'diners'
  | 'troy'
  | 'mir'
  | 'carnet'

export const OUR_CONSTANTS_MAP_TO_PREPARE_CARD_TYPE: Record<CreditCardType, PrepareCreditCardType> =
  {
    [CreditCardType.Mastercard]: 'master-card',
    [CreditCardType.Amex]: 'amex',
    [CreditCardType.Visa]: 'visa',
    [CreditCardType.Maestro]: 'maestro',
    [CreditCardType.Discover]: 'discover',
    [CreditCardType.Jcb]: 'jcb',
    [CreditCardType.Dankort]: 'dankort',
    [CreditCardType.Switch]: 'switch',
    [CreditCardType.Diners]: 'diners',
    [CreditCardType.Troy]: 'troy',
    [CreditCardType.Mir]: 'mir',
    [CreditCardType.Carnet]: 'carnet',
  } as const

export const PREPARE_CARD_TYPE_TO_OUR_CONSTANTS_MAP = invertObj(
  OUR_CONSTANTS_MAP_TO_PREPARE_CARD_TYPE,
) as Record<PrepareCreditCardType, CreditCardType>

export type CreditCardTypeName =
  | 'Mastercard'
  | 'Visa'
  | 'Amex'
  | 'Maestro'
  | 'Discover'
  | 'JCB'
  | 'Switch'
  | 'Dankort'
  | 'Diners'
  | 'Troy'
  | 'MIR'
  | 'Carnet'

export const CREDIT_CARD_NAME_MAPPING: Record<CreditCardType, CreditCardTypeName> = {
  [CreditCardType.Mastercard]: 'Mastercard',
  [CreditCardType.Visa]: 'Visa',
  [CreditCardType.Amex]: 'Amex',
  [CreditCardType.Maestro]: 'Maestro',
  [CreditCardType.Discover]: 'Discover',
  [CreditCardType.Jcb]: 'JCB',
  [CreditCardType.Switch]: 'Switch',
  [CreditCardType.Dankort]: 'Dankort',
  [CreditCardType.Diners]: 'Diners',
  [CreditCardType.Troy]: 'Troy',
  [CreditCardType.Mir]: 'MIR',
  [CreditCardType.Carnet]: 'Carnet',
} as const

export const CREDIT_CARD_ICON_MAPPING: Record<CreditCardType, React.ComponentType<IconProps>> = {
  [CreditCardType.Mastercard]: MastercardIcon,
  [CreditCardType.Visa]: VISAIcon,
  [CreditCardType.Amex]: AmexIcon,
  [CreditCardType.Maestro]: MaestroIcon,
  [CreditCardType.Discover]: DiscoverIcon,
  [CreditCardType.Jcb]: JCBIcon,
  [CreditCardType.Troy]: TroyIcon,
  [CreditCardType.Mir]: MIRIcon,
  [CreditCardType.Carnet]: CarnetIcon,

  // TODO: add icons
  [CreditCardType.Switch]: () => null,
  [CreditCardType.Dankort]: () => null,
  [CreditCardType.Diners]: () => null,
} as const

export const ADYEN_FORM_ID = 'adyen_form_id' as const
export const CYBERSOURCE_FORM_ID = 'cybersource_form_id' as const

export const VAT_ATTRIBUTE = 'vat' as const
